import {
	createContext,
	useContext,
	type ReactNode,
} from "react";

// =====================
// Types
// =====================

export interface ToastMessage {
	id: string;
	variant: "success" | "error" | "warning" | "info";
	title: string;
	description?: string;
	duration?: number;
	action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
	toasts: ToastMessage[];
	addToast: {
		(toast: Omit<ToastMessage, "id">): string;
		(title: string, variant: ToastMessage["variant"]): string;
	};
	removeToast: (id: string) => void;
	success: (title: string, description?: string) => string;
	error: (title: string, description?: string) => string;
	warning: (title: string, description?: string) => string;
	info: (title: string, description?: string) => string;
	clearAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

import { useState, useCallback, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { CircleCheck, CircleX, AlertTriangle, Info, X } from "lucide-react";

const iconMap: Record<ToastMessage["variant"], React.ComponentType<{ className?: string }>> = {
	success: CircleCheck,
	error: CircleX,
	warning: AlertTriangle,
	info: Info,
};

const colorConfig: Record<ToastMessage["variant"], { bg: string; border: string; icon: string; title: string; description: string; progress: string }> = {
	success: { bg: "bg-green-50 dark:bg-green-950", border: "border-green-200 dark:border-green-800", icon: "text-green-500 dark:text-green-400", title: "text-green-900 dark:text-green-100", description: "text-green-700 dark:text-green-300", progress: "bg-green-500" },
	error: { bg: "bg-red-50 dark:bg-red-950", border: "border-red-200 dark:border-red-800", icon: "text-red-500 dark:text-red-400", title: "text-red-900 dark:text-red-100", description: "text-red-700 dark:text-red-300", progress: "bg-red-500" },
	warning: { bg: "bg-yellow-50 dark:bg-yellow-950", border: "border-yellow-200 dark:border-yellow-800", icon: "text-yellow-500 dark:text-yellow-400", title: "text-yellow-900 dark:text-yellow-100", description: "text-yellow-700 dark:text-yellow-300", progress: "bg-yellow-500" },
	info: { bg: "bg-blue-50 dark:bg-blue-950", border: "border-blue-200 dark:border-blue-800", icon: "text-blue-500 dark:text-blue-400", title: "text-blue-900 dark:text-blue-100", description: "text-blue-700 dark:text-blue-300", progress: "bg-blue-500" },
};

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
	const colors = colorConfig[toast.variant];
	const Icon = iconMap[toast.variant];
	const duration = toast.duration ?? 5000;

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: 50, scale: 0.95 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			exit={{ opacity: 0, y: 20, scale: 0.95 }}
			transition={{ duration: 0.2 }}
			className={cn("relative w-full max-w-sm overflow-hidden rounded-lg border shadow-lg", colors.bg, colors.border)}
			role="alert"
		>
			<div className="flex gap-3 p-4">
				<Icon className={cn("w-5 h-5 flex-shrink-0 mt-0.5", colors.icon)} />
				<div className="flex-1 min-w-0">
					<h4 className={cn("text-sm font-semibold", colors.title)}>{toast.title}</h4>
					{toast.description && <p className={cn("mt-1 text-sm", colors.description)}>{toast.description}</p>}
					{toast.action && <button onClick={toast.action.onClick} className={cn("mt-2 text-sm font-medium underline underline-offset-2", colors.title, "hover:opacity-80")}>{toast.action.label}</button>}
				</div>
				<button onClick={onDismiss} className="flex-shrink-0 p-1 rounded-md transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10" aria-label="Dismiss">
					<X className="w-4 h-4" />
				</button>
			</div>
			{duration > 0 && <motion.div initial={{ scaleX: 1 }} animate={{ scaleX: 0 }} transition={{ duration: duration / 1000, ease: "linear" }} className={cn("h-1 origin-left", colors.progress)} />}
		</motion.div>
	);
}

function ToastContainer() {
	const context = useContext(ToastContext);
	if (!context) return null;
	const { toasts, removeToast } = context;
	if (toasts.length === 0) return null;
	return (
		<div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-h-[calc(100vh-2rem)]" aria-live="polite" aria-label="Notifications">
			<AnimatePresence mode="popLayout">
				{toasts.map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />)}
			</AnimatePresence>
		</div>
	);
}

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<ToastMessage[]>([]);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	const addToast = useCallback((toastOrTitle: Omit<ToastMessage, "id"> | string, variant?: ToastMessage["variant"]) => {
		const toastObj: Omit<ToastMessage, "id"> = typeof toastOrTitle === "string"
			? { title: toastOrTitle, variant: variant || "info" }
			: toastOrTitle;
		const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
		const duration = toastObj.duration ?? 5000;
		setToasts((prev) => [...prev, { ...toastObj, id }]);
		if (duration > 0) setTimeout(() => removeToast(id), duration);
		return id;
	}, [removeToast]);

	const success = useCallback((title: string, description?: string) => addToast({ variant: "success", title, description }), [addToast]);
	const error = useCallback((title: string, description?: string) => addToast({ variant: "error", title, description, duration: 7000 }), [addToast]);
	const warning = useCallback((title: string, description?: string) => addToast({ variant: "warning", title, description }), [addToast]);
	const info = useCallback((title: string, description?: string) => addToast({ variant: "info", title, description }), [addToast]);
	const clearAll = useCallback(() => setToasts([]), []);

	return (
		<ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info, clearAll }}>
			{children}
			<ToastContainer />
		</ToastContext.Provider>
	);
}

export function useToast() {
	const context = useContext(ToastContext);
	if (!context) {
		// Provide a fallback implementation if Toast provider is not available
		// This handles cases where components using useToast are rendered before provider is ready
		return {
			toasts: [],
			addToast: (toastOrTitle: Omit<ToastMessage, "id"> | string, variant?: ToastMessage["variant"]) => {
				const title = typeof toastOrTitle === "string" ? toastOrTitle : toastOrTitle.title;
				if (typeof toastOrTitle !== "string") variant = toastOrTitle.variant;
				console.log("[Toast]", title, variant);
				return "";
			},
			removeToast: () => {},
			success: (title: string) => {
				console.log("[Toast Success]", title);
				return "";
			},
			error: (title: string) => {
				console.error("[Toast Error]", title);
				return "";
			},
			warning: (title: string) => {
				console.warn("[Toast Warning]", title);
				return "";
			},
			info: (title: string) => {
				console.info("[Toast Info]", title);
				return "";
			},
			clearAll: () => {},
		};
	}
	return context;
}